import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';
import Cookies from 'js-cookie';
import useSearchParams from 'hooks/useSearchParams';
import { updateShowPasswordQualifyStatus } from 'client/redux/modules/promoState';
import { showCustomErrDialog, handlers } from 'client/redux/modules/systemErrorDialog';
import useClientHistory, { RESULT } from 'client/hooks/useClientHistory';
import { getLogInInfoAPI } from 'client/utils/tokenManager';
import checkPasscodeVerify from 'client/utils/passcode/checkPasscodeVerify';
import { loginTypesForApi, loginMethodMappingLoginTypeForApi } from 'config/loginTypes';
import { errorTypes, getErrorTypeDialogDesc } from 'config/passcode';

// hasVerifiedPasscodeFirstTime 用來控制此 hook 的 useEffect 會不會每次都自動打 verify passcode api
// 因為很多地方都會用到此，但 useEffect 只會在頁面剛載入呼叫，所以其他時間呼叫此 hook 不需要執行 useEffect 的 verify passcode api
const usePasscodeManager = ({ hasVerifiedPasscodeFirstTime = false } = {}) => {
    const history = useClientHistory();
    const loginType = useSelector(s => s.settingPromo.loginMethod);
    const promoState = useSelector(s => s.promoState);
    const {
        enablePasscodeVerify,
        passcode: passcodeFromRedux,
        hasUsablePasscode,
        isPurePrizeGame
    } = promoState;
    const promoUUID = useSelector(s => s.promo.uuid);
    const isLogin = useSelector(s => s.userLoginState.isLogin);

    // 新增：獲取用於計算 shouldRedirectToResultPage 的 state
    const {
        hasGameResult,
        hasFilledSignupForm,
        hasParticipantPrize,
        hasInstantPrize
    } = useSelector(s => s.userEntryState);
    const userGameResultRecords = useSelector(s => s.userGameResultRecords);

    // 獲取初始化狀態
    const { hasUserStateInited } = useSelector(s => s.flags);

    const userWinnerPrize = useSelector(s => s.userWinnerPrize);

    // 參考 useUserGamePhaseRedirectOnInit，計算 shouldRedirectToResultPage
    const shouldRedirectToResultPage = isPurePrizeGame
        ? hasGameResult && hasFilledSignupForm && hasParticipantPrize && hasInstantPrize
        : hasGameResult;

    const dispatch = useDispatch();
    const [cookieKey, setCookieKey] = useState('{}');
    const [isShowReCaptcha, setShowReCaptcha] = useState(false);
    const [memberLoaded, setMemberLoaded] = useState(false);
    const [hasTriedVerifyPasscode, setHasTriedVerifyPasscode] = useState(false);
    const searchParams = useSearchParams();
    const passcodeFromUrl = searchParams.get('pcode') || '';
    const hasPcodeInUrl = searchParams.has('pcode');

    const getPasscode = useCallback(() => {
        return Cookies.get(cookieKey);
    }, [cookieKey]);

    const setPasscode = useCallback(
        passcode => {
            Cookies.set(cookieKey, passcode);
        },
        [cookieKey]
    );

    const clearPasscode = useCallback(() => {
        Cookies.remove(cookieKey);
    }, [cookieKey]);

    const getPasscodeList = useCallback(() => {
        try {
            const cookieValue = Cookies.get(cookieKey);
            return cookieValue ? JSON.parse(cookieValue) : [];
        } catch (error) {
            console.error('Error parsing passcodeList cookie:', error);
            return [];
        }
    }, [cookieKey]);

    const updatePasscodeList = useCallback(
        passcode => {
            if (!passcode) return;

            try {
                const originalPasscodeList = getPasscodeList();
                if (!originalPasscodeList.includes(passcode)) {
                    Cookies.set(cookieKey, JSON.stringify([...originalPasscodeList, passcode]));
                }
            } catch (error) {
                console.error('Error updating passcodeList cookie:', error);
            }
        },
        [cookieKey, getPasscodeList]
    );

    const removePasscode = useCallback(
        passcode => {
            if (!passcode) return;

            try {
                const originalPasscodeList = getPasscodeList();
                if (originalPasscodeList.length === 0) {
                    clearPasscode();
                } else {
                    Cookies.set(
                        cookieKey,
                        JSON.stringify(originalPasscodeList.filter(p => p !== passcode))
                    );
                }
            } catch (error) {
                console.error('Error removing passcode from cookie:', error);
            }
        },
        [clearPasscode, cookieKey, getPasscodeList]
    );

    const verifyPasscode = useCallback(
        async (passcode, recaptchaToken) => {
            if (!passcode) return false;

            // 確保 member 數據已載入
            if (!memberLoaded) {
                console.log('Member data not loaded yet, cannot verify passcode');
                return false;
            }
            const entryRecordHasUnOpenWinner = userWinnerPrize.unOpenWinnerPrize.some(
                unOpenWinner =>
                    userGameResultRecords.find(record => record.uuid === unOpenWinner.entryUuid)
            );

            if (entryRecordHasUnOpenWinner) return true;

            const passcodeUnverifiedCode = await checkPasscodeVerify(
                {
                    promo: { uuid: promoUUID },
                    promoState: { enablePasscodeVerify, passcode }
                },
                promoState => dispatch(updateShowPasswordQualifyStatus(promoState)),
                recaptchaToken
            );

            // passcode 驗證沒過，則移除 passcode
            if (passcodeUnverifiedCode) {
                if (passcodeUnverifiedCode === errorTypes.RECAPTCHA_TOK_AUTH_FAILED)
                    setShowReCaptcha(true);
                else {
                    removePasscode(passcode);
                    const isEntryHasGameResult = userGameResultRecords.some(record =>
                        Boolean(record?.flow?.gameResult)
                    );
                    if (
                        passcodeUnverifiedCode === errorTypes.USAGE_LIMIT_EXCEEDED &&
                        isEntryHasGameResult
                    ) {
                        history.push(RESULT);
                        return true;
                    }

                    // 修改：實時檢查是否應該導向 result page，避免競跑條件
                    if (hasPcodeInUrl && !shouldRedirectToResultPage) {
                        dispatch(
                            showCustomErrDialog({
                                title: i18n.t('label.passcode.inuse'),
                                desc: getErrorTypeDialogDesc(passcodeUnverifiedCode),
                                handler: handlers.PASSCODE_UNVERIFIED
                            })
                        );
                    }
                    return false;
                }
                return false;
            }
            return true;
        },
        [
            dispatch,
            enablePasscodeVerify,
            hasPcodeInUrl,
            history,
            memberLoaded,
            promoUUID,
            removePasscode,
            shouldRedirectToResultPage,
            userGameResultRecords,
            userWinnerPrize
        ]
    );

    const latestPasscodeFromCookie = getPasscodeList().pop();
    const passcode = passcodeFromUrl || passcodeFromRedux || latestPasscodeFromCookie;

    useEffect(() => {
        async function getMemberInfo() {
            try {
                const resp = await getLogInInfoAPI({ loginType });
                const getMember = loginType => {
                    if (loginType === loginTypesForApi.ANONYMOUS) {
                        return resp[loginType];
                    }
                    const loginTypeData = resp[loginType];
                    if (!loginTypeData) {
                        console.error('Login type data not found:', loginType);
                        return null;
                    }
                    const firstProvider = Object.values(loginTypeData)[0];
                    if (!firstProvider?.user) {
                        console.error('User data not found in provider:', firstProvider);
                        return null;
                    }
                    return firstProvider.user;
                };

                const member = getMember(loginMethodMappingLoginTypeForApi[loginType]);

                if (!member) {
                    console.error('Failed to get member info');
                    return;
                }

                setCookieKey(`pcode-${member.member_uuid}-${promoUUID}`);
                setMemberLoaded(true);
            } catch (error) {
                console.error('Error getting member info:', error);
            }
        }
        if (enablePasscodeVerify && isLogin) {
            getMemberInfo();
        }
    }, [enablePasscodeVerify, isLogin, loginType, promoUUID]);

    // 每次玩遊戲前，都需要 verify 這次要用到的 passcode 加上取得 passcode 可用的剩餘次數
    // passcode 優先權: qs > redux > cookie
    useEffect(() => {
        const shouldVerify =
            enablePasscodeVerify &&
            passcode &&
            !hasVerifiedPasscodeFirstTime &&
            !hasUsablePasscode &&
            memberLoaded &&
            !hasTriedVerifyPasscode;

        if (shouldVerify) {
            // 如果有 URL 中的 passcode，等待用戶狀態初始化完成後再驗證
            if (hasPcodeInUrl) {
                // 確認 redux 的 userEntryState 已經初始化完成，才能避免競跑條件
                // 此時 shouldRedirectToResultPage 是 true 或 false 都已經確定，不會再改變
                if (hasUserStateInited) {
                    setHasTriedVerifyPasscode(true);
                    verifyPasscode(passcode);
                }
            } else {
                // 沒有 URL passcode 的情況下直接驗證，不需等待 userEntryState 初始化完成
                setHasTriedVerifyPasscode(true);
                verifyPasscode(passcode);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        enablePasscodeVerify,
        passcode,
        hasVerifiedPasscodeFirstTime,
        memberLoaded,
        hasTriedVerifyPasscode,
        hasPcodeInUrl,
        hasUserStateInited
    ]);

    return {
        isShowReCaptcha,
        setShowReCaptcha,
        enablePasscodeVerify,
        currentInUsePasscode: passcode,
        cookieKey,
        memberLoaded,
        passcode,
        getPasscode,
        setPasscode,
        clearPasscode,
        getPasscodeList,
        updatePasscodeList,
        removePasscode,
        verifyPasscode,
        continueVerifyPasscode: token => {
            verifyPasscode(passcode, token);
        }
    };
};

export default usePasscodeManager;
